import { motion } from 'framer-motion';
import { theme } from '../../theme';
import { landing } from '../../language';
import { useMobile } from '../../hooks/useMediaQuery';

interface LandingPageProps {
  onEnter: () => void;
}

/**
 * Full-bleed landing page.
 * The skull illustration IS the page.
 * Flat. No gradients. No glass. Military poster.
 */
export const LandingPage = ({ onEnter }: LandingPageProps) => {
  const mobile = useMobile();

  return (
    <div
      style={{
        minHeight: '100vh',
        background: theme.brand.crimson,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        padding: mobile
          ? `${theme.spacing[8]} ${theme.spacing[4]}`
          : `${theme.spacing[12]} ${theme.spacing[6]}`,
      }}
      onClick={onEnter}
    >
      {/* Top copper bar */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: mobile ? '4px' : '6px',
        background: theme.brand.copper,
      }} />

      {/* Skull illustration */}
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        style={{ marginBottom: theme.spacing[4] }}
      >
        <img
          src="/skull.png"
          alt="Debt Fight Club"
          style={{
            width: mobile ? '200px' : '320px',
            height: 'auto',
            display: 'block',
          }}
        />
      </motion.div>

      {/* Title block */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        style={{ textAlign: 'center' }}
      >
        <h1 style={{
          fontFamily: theme.typography.fontFamily.display,
          fontSize: mobile ? theme.typography.fontSize['4xl'] : theme.typography.fontSize['6xl'],
          fontWeight: theme.typography.fontWeight.black,
          color: theme.brand.cream,
          letterSpacing: theme.typography.letterSpacing.widest,
          textTransform: 'uppercase',
          lineHeight: theme.typography.lineHeight.tight,
          margin: 0,
        }}>
          {landing.title[0]}<br />{landing.title[1]}
        </h1>

        {/* Copper divider */}
        <div style={{
          width: mobile ? '60px' : '80px',
          height: '4px',
          background: theme.brand.copper,
          margin: `${theme.spacing[3]} auto`,
        }} />

        <p style={{
          fontFamily: theme.typography.fontFamily.display,
          fontSize: mobile ? theme.typography.fontSize.sm : theme.typography.fontSize.lg,
          fontWeight: theme.typography.fontWeight.medium,
          color: theme.brand.cream,
          letterSpacing: theme.typography.letterSpacing.wider,
          textTransform: 'uppercase',
          opacity: 0.7,
          margin: 0,
        }}>
          {landing.subtitle}
        </p>
      </motion.div>

      {/* Enter button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        whileTap={{ scale: 0.97 }}
        onClick={(e) => { e.stopPropagation(); onEnter(); }}
        style={{
          marginTop: theme.spacing[4],
          padding: mobile
            ? `${theme.spacing[3]} ${theme.spacing[6]}`
            : `${theme.spacing[3]} ${theme.spacing[10]}`,
          background: theme.brand.copper,
          border: `${theme.borders.thick} ${theme.brand.cream}`,
          borderRadius: theme.radii.none,
          color: theme.brand.cream,
          fontFamily: theme.typography.fontFamily.display,
          fontSize: mobile ? theme.typography.fontSize.sm : theme.typography.fontSize.base,
          fontWeight: theme.typography.fontWeight.bold,
          letterSpacing: theme.typography.letterSpacing.widest,
          textTransform: 'uppercase' as const,
          cursor: 'pointer',
          minHeight: '48px', // touch target
        }}
      >
        {landing.cta.toUpperCase()}
      </motion.button>

      {/* Bottom copper bar */}
      <div style={{
        position: 'absolute',
        bottom: 0, left: 0, right: 0,
        height: mobile ? '4px' : '6px',
        background: theme.brand.copper,
      }} />

      {/* Corner marks */}
      {!mobile && ['top-left', 'top-right', 'bottom-left', 'bottom-right'].map((pos) => {
        const [v, h] = pos.split('-');
        return (
          <div
            key={pos}
            style={{
              position: 'absolute',
              [v]: '24px',
              [h]: '24px',
              width: '20px',
              height: '20px',
              borderColor: theme.brand.cream,
              borderStyle: 'solid',
              borderWidth: 0,
              ...(v === 'top' ? { borderTopWidth: '2px' } : { borderBottomWidth: '2px' }),
              ...(h === 'left' ? { borderLeftWidth: '2px' } : { borderRightWidth: '2px' }),
              opacity: 0.3,
            }}
          />
        );
      })}
    </div>
  );
};

export default LandingPage;
